'use client';

import React, { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { formatNPR } from '@/lib/i18n';

interface Restaurant {
  id: string;
  name: string;
  location: string;
  city?: string;
  cuisine_type?: string;
  cuisines?: string[];
  description?: string;
  image_url?: string;
  rating?: number;
  reviews_count?: number;
  price_range?: string; // 'budget' | 'mid' | 'upscale' | 'fine_dining'
  avg_price_per_person?: number;
  opening_hours?: string;
  is_featured?: boolean;
  is_verified?: boolean;
  phone?: string;
  address?: string;
  tags?: string[];
}

const CUISINES = [
  'All',
  'Nepali',
  'Tibetan',
  'Indian',
  'Chinese',
  'Continental',
  'Italian',
  'Japanese',
  'Thai',
  'Mexican',
  'Fast Food',
  'Café',
  'Bakery',
  'Seafood',
  'Vegetarian',
  'Vegan',
];

const PRICE_RANGES = [
  { value: 'all', label: 'Any Price' },
  { value: 'budget', label: '₹ Budget' },
  { value: 'mid', label: '₹₹ Mid-range' },
  { value: 'upscale', label: '₹₹₹ Upscale' },
  { value: 'fine_dining', label: '₹₹₹₹ Fine Dining' },
];

const SORT_OPTIONS = [
  { value: 'rating', label: 'Top Rated' },
  { value: 'featured', label: 'Featured' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
];

const DEMO_RESTAURANTS: Restaurant[] = [
  {
    id: 'krishnarpan-dwarika',
    name: "Krishnarpan at Dwarika's",
    location: 'Battisputali, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Nepali',
    cuisines: ['Nepali', 'Continental'],
    description: 'Award-winning fine dining serving authentic Nepali cuisine in a heritage setting. Multi-course set menus celebrating Nepal\'s diverse culinary traditions.',
    image_url: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80',
    rating: 4.9,
    reviews_count: 1240,
    price_range: 'fine_dining',
    avg_price_per_person: 4500,
    opening_hours: '12:00 PM – 10:00 PM',
    is_featured: true,
    is_verified: true,
    tags: ['Heritage', 'Set Menu', 'Award Winning'],
  },
  {
    id: 'the-chimney-yak-yeti',
    name: 'The Chimney Restaurant',
    location: 'Durbar Marg, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Continental',
    cuisines: ['Continental', 'Russian'],
    description: 'Kathmandu\'s oldest fine dining restaurant, famous for its Russian cuisine and the iconic chimney grill. A landmark since 1963.',
    image_url: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600&q=80',
    rating: 4.7,
    reviews_count: 890,
    price_range: 'upscale',
    avg_price_per_person: 3200,
    opening_hours: '11:00 AM – 10:30 PM',
    is_featured: true,
    is_verified: true,
    tags: ['Historic', 'Grill', 'Landmark'],
  },
  {
    id: 'roadhouse-cafe-thamel',
    name: 'Roadhouse Café',
    location: 'Thamel, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Italian',
    cuisines: ['Italian', 'Continental', 'Café'],
    description: 'Popular wood-fired pizza and pasta restaurant in the heart of Thamel. Great for trekkers and travellers seeking familiar comfort food.',
    image_url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600&q=80',
    rating: 4.5,
    reviews_count: 2100,
    price_range: 'mid',
    avg_price_per_person: 1200,
    opening_hours: '10:00 AM – 11:00 PM',
    is_featured: false,
    is_verified: true,
    tags: ['Pizza', 'Pasta', 'Trekker Friendly'],
  },
  {
    id: 'moondance-pokhara',
    name: 'Moondance Restaurant',
    location: 'Lakeside, Pokhara',
    city: 'Pokhara',
    cuisine_type: 'Continental',
    cuisines: ['Continental', 'Nepali', 'Indian'],
    description: 'Lakeside gem with stunning Phewa Lake views. Serves a wide menu from dal bhat to pasta, perfect for sunset dinners.',
    image_url: 'https://images.unsplash.com/photo-1466978913421-dad2ebd01d17?w=600&q=80',
    rating: 4.6,
    reviews_count: 1560,
    price_range: 'mid',
    avg_price_per_person: 900,
    opening_hours: '8:00 AM – 10:00 PM',
    is_featured: true,
    is_verified: true,
    tags: ['Lake View', 'Sunset', 'Romantic'],
  },
  {
    id: 'busy-bee-cafe-pokhara',
    name: 'Busy Bee Café',
    location: 'Lakeside, Pokhara',
    city: 'Pokhara',
    cuisine_type: 'Café',
    cuisines: ['Café', 'Bakery', 'Continental'],
    description: 'Cosy café known for its fresh baked goods, specialty coffee, and live music evenings. A favourite among backpackers and digital nomads.',
    image_url: 'https://images.unsplash.com/photo-1501339847302-ac426a4a7cbb?w=600&q=80',
    rating: 4.4,
    reviews_count: 780,
    price_range: 'budget',
    avg_price_per_person: 450,
    opening_hours: '7:00 AM – 9:00 PM',
    is_featured: false,
    is_verified: true,
    tags: ['Coffee', 'Live Music', 'Bakery'],
  },
  {
    id: 'thakali-kitchen-kathmandu',
    name: 'Thakali Kitchen',
    location: 'New Road, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Nepali',
    cuisines: ['Nepali', 'Tibetan'],
    description: 'Authentic Thakali set meals served in traditional style. Famous for their ghee-drizzled dal bhat and homemade pickles.',
    image_url: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=600&q=80',
    rating: 4.8,
    reviews_count: 3200,
    price_range: 'budget',
    avg_price_per_person: 350,
    opening_hours: '10:00 AM – 9:00 PM',
    is_featured: false,
    is_verified: true,
    tags: ['Dal Bhat', 'Thakali', 'Local Favourite'],
  },
  {
    id: 'garden-of-dreams-cafe',
    name: 'Garden of Dreams Café',
    location: 'Kaiser Mahal, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Continental',
    cuisines: ['Continental', 'Café', 'Bakery'],
    description: 'Nestled inside the historic Garden of Dreams, this café offers a tranquil escape with European-style food and afternoon tea.',
    image_url: 'https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=600&q=80',
    rating: 4.5,
    reviews_count: 640,
    price_range: 'upscale',
    avg_price_per_person: 1800,
    opening_hours: '9:00 AM – 8:00 PM',
    is_featured: true,
    is_verified: true,
    tags: ['Garden', 'Heritage', 'Afternoon Tea'],
  },
  {
    id: 'new-everest-steak-house',
    name: 'New Everest Steak House',
    location: 'Thamel, Kathmandu',
    city: 'Kathmandu',
    cuisine_type: 'Continental',
    cuisines: ['Continental', 'Nepali'],
    description: 'Legendary steak house in Thamel serving buffalo and yak steaks alongside classic Nepali dishes. A trekker\'s post-expedition reward.',
    image_url: 'https://images.unsplash.com/photo-1544025162-d76694265947?w=600&q=80',
    rating: 4.3,
    reviews_count: 1890,
    price_range: 'mid',
    avg_price_per_person: 1400,
    opening_hours: '11:00 AM – 10:00 PM',
    is_featured: false,
    is_verified: true,
    tags: ['Steak', 'Trekker Favourite', 'Yak Meat'],
  },
];

function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) {
  const stars = Math.round(rating);
  return (
    <div className={`flex items-center gap-0.5 ${size === 'md' ? 'text-base' : 'text-sm'}`}>
      {[1, 2, 3, 4, 5].map(i => (
        <span key={i} className={i <= stars ? 'text-amber-400' : 'text-gray-300'}>★</span>
      ))}
    </div>
  );
}

function PriceRangeTag({ range }: { range?: string }) {
  const map: Record<string, { label: string; color: string }> = {
    budget: { label: '₹ Budget', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    mid: { label: '₹₹ Mid-range', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    upscale: { label: '₹₹₹ Upscale', color: 'bg-purple-50 text-purple-700 border-purple-200' },
    fine_dining: { label: '₹₹₹₹ Fine Dining', color: 'bg-amber-50 text-amber-700 border-amber-200' },
  };
  const info = map[range || 'mid'] || map['mid'];
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${info.color}`}>{info.label}</span>
  );
}

export default function RestaurantsPage() {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedCuisine, setSelectedCuisine] = useState('All');
  const [selectedPriceRange, setSelectedPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [minRating, setMinRating] = useState(0);

  useEffect(() => {
    const fetchRestaurants = async () => {
      setLoading(true);
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('restaurants')
          .select('*')
          .order('is_featured', { ascending: false })
          .order('rating', { ascending: false });
        if (data && data.length > 0) {
          setRestaurants(data);
        } else {
          setRestaurants(DEMO_RESTAURANTS);
        }
      } catch {
        setRestaurants(DEMO_RESTAURANTS);
      }
      setLoading(false);
    };
    fetchRestaurants();
  }, []);

  const filtered = useMemo(() => {
    let list = [...restaurants];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.name?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.cuisine_type?.toLowerCase().includes(q) ||
        r.tags?.some(t => t.toLowerCase().includes(q))
      );
    }

    if (locationSearch) {
      const q = locationSearch.toLowerCase();
      list = list.filter(r =>
        r.location?.toLowerCase().includes(q) ||
        r.city?.toLowerCase().includes(q) ||
        r.address?.toLowerCase().includes(q)
      );
    }

    if (selectedCuisine !== 'All') {
      list = list.filter(r =>
        r.cuisine_type === selectedCuisine ||
        r.cuisines?.includes(selectedCuisine)
      );
    }

    if (selectedPriceRange !== 'all') {
      list = list.filter(r => r.price_range === selectedPriceRange);
    }

    if (minRating > 0) {
      list = list.filter(r => (r.rating || 0) >= minRating);
    }

    switch (sortBy) {
      case 'rating':
        list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'featured':
        list.sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
        break;
      case 'price_asc':
        list.sort((a, b) => (a.avg_price_per_person || 0) - (b.avg_price_per_person || 0));
        break;
      case 'price_desc':
        list.sort((a, b) => (b.avg_price_per_person || 0) - (a.avg_price_per_person || 0));
        break;
    }

    return list;
  }, [restaurants, search, locationSearch, selectedCuisine, selectedPriceRange, sortBy, minRating]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-orange-600 via-red-600 to-rose-700 text-white pt-24 pb-16 px-4">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1400&q=60)', backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 text-sm font-medium mb-4">
            🍽️ Dining in Nepal
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Discover Restaurants & Cafés</h1>
          <p className="text-lg text-white/80 max-w-xl mx-auto mb-8">
            From authentic dal bhat to fine dining — reserve your table at Nepal's best restaurants.
          </p>

          {/* Search bar */}
          <div className="bg-white rounded-2xl shadow-xl p-3 flex flex-col md:flex-row gap-3 max-w-3xl mx-auto">
            <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5">
              <span className="text-gray-400 text-lg">🔍</span>
              <input
                type="text"
                placeholder="Search restaurants, cuisines, dishes..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-sm outline-none"
              />
            </div>
            <div className="flex-1 flex items-center gap-2 bg-gray-50 rounded-xl px-4 py-2.5">
              <span className="text-gray-400 text-lg">📍</span>
              <input
                type="text"
                placeholder="Location (e.g. Pokhara, Thamel...)"
                value={locationSearch}
                onChange={e => setLocationSearch(e.target.value)}
                className="flex-1 bg-transparent text-gray-800 placeholder-gray-400 text-sm outline-none"
              />
            </div>
            <button className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-2.5 rounded-xl transition-colors text-sm">
              Search
            </button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto w-full px-4 py-8 flex-1">
        {/* Cuisine Filter Chips */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide">
          {CUISINES.map(c => (
            <button
              key={c}
              onClick={() => setSelectedCuisine(c)}
              className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                selectedCuisine === c
                  ? 'bg-orange-600 text-white border-orange-600 shadow-sm'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 font-medium">Price:</label>
            <select
              value={selectedPriceRange}
              onChange={e => setSelectedPriceRange(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 outline-none focus:border-orange-400"
            >
              {PRICE_RANGES.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500 font-medium">Min Rating:</label>
            <select
              value={minRating}
              onChange={e => setMinRating(Number(e.target.value))}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 outline-none focus:border-orange-400"
            >
              <option value={0}>Any</option>
              <option value={3}>3+ ★</option>
              <option value={4}>4+ ★</option>
              <option value={4.5}>4.5+ ★</option>
            </select>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <label className="text-sm text-gray-500 font-medium">Sort:</label>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-700 outline-none focus:border-orange-400"
            >
              {SORT_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <span className="text-sm text-gray-400">{filtered.length} restaurant{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm animate-pulse">
                <div className="h-48 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🍽️</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No restaurants found</h3>
            <p className="text-gray-500">Try adjusting your filters or search terms.</p>
            <button
              onClick={() => { setSearch(''); setLocationSearch(''); setSelectedCuisine('All'); setSelectedPriceRange('all'); setMinRating(0); }}
              className="mt-4 text-orange-600 hover:underline text-sm font-medium"
            >
              Clear all filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(r => (
              <RestaurantCard key={r.id} restaurant={r} />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}

function RestaurantCard({ restaurant: r }: { restaurant: Restaurant }) {
  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={r.image_url || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80'}
          alt={`${r.name} — ${r.cuisine_type || 'restaurant'} in ${r.location}`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {r.is_featured && (
            <span className="bg-orange-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full">Featured</span>
          )}
          {r.is_verified && (
            <span className="bg-white/90 text-green-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
              <span>✓</span> Verified
            </span>
          )}
        </div>
        {r.cuisine_type && (
          <div className="absolute bottom-3 right-3">
            <span className="bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
              {r.cuisine_type}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-semibold text-gray-900 text-base leading-tight">{r.name}</h3>
          <PriceRangeTag range={r.price_range} />
        </div>

        <div className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
          <span>📍</span>
          <span>{r.location}</span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 mb-2">
          <StarRating rating={r.rating || 0} />
          <span className="text-sm font-semibold text-gray-800">{r.rating?.toFixed(1)}</span>
          <span className="text-xs text-gray-400">({r.reviews_count?.toLocaleString()} reviews)</span>
        </div>

        {r.description && (
          <p className="text-sm text-gray-500 line-clamp-2 mb-3">{r.description}</p>
        )}

        {/* Tags */}
        {r.tags && r.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {r.tags.slice(0, 3).map(tag => (
              <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            {r.avg_price_per_person ? (
              <div className="text-sm">
                <span className="font-semibold text-gray-900">{formatNPR(r.avg_price_per_person)}</span>
                <span className="text-gray-400 text-xs"> /person avg</span>
              </div>
            ) : null}
            {r.opening_hours && (
              <div className="text-xs text-gray-400 mt-0.5">🕐 {r.opening_hours}</div>
            )}
          </div>
          <Link
            href={`/restaurants/${r.id}/book`}
            className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
          >
            Reserve Table
          </Link>
        </div>
      </div>
    </div>
  );
}
